import React, { useRef, useState, useEffect } from 'react';
import Papa from 'papaparse';
import Chart from 'chart.js/auto';
import { generateFilename, getWeekStart } from '../../lib/dateUtils';
import { Camera } from 'lucide-react';

// Faithful port: all-in-one, minimal React-ification
const initialStatus = { message: '', type: '', visible: false };

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const timeSlots = [
  'EARLY MORNING\n6:00 - 9:00 AM',
  'LATE MORNING\n9:00 - 12:00 PM',
  'MIDDAY\n12:00 - 3:00 PM',
  'AFTERNOON\n3:00 - 6:00 PM',
  'EVENING\n6:00 - 9:00 PM',
  'NIGHT\n9:00 PM+'
];

function getTimeSlot(hour: number) {
  // Validate input
  if (isNaN(hour) || hour < 0 || hour > 23) {
    console.warn('Invalid hour value:', hour);
    return 5; // Default to night slot
  }
  
  if (hour >= 6 && hour < 9) return 0;
  if (hour >= 9 && hour < 12) return 1;
  if (hour >= 12 && hour < 15) return 2;
  if (hour >= 15 && hour < 18) return 3;
  if (hour >= 18 && hour < 21) return 4;
  return 5;
}

function formatDate(date: Date) {
  return date.toLocaleDateString();
}

const HealthReportGenerator: React.FC = () => {
  const [allData, setAllData] = useState<any[]>([]);
  const [status, setStatus] = useState(initialStatus);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [weeklyData, setWeeklyData] = useState<any>({});
  const [weeks, setWeeks] = useState<any[]>([]);
  const [chartsReady, setChartsReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFilename, setExportFilename] = useState('diabetes-lifestyle-report');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const glucoseChartRef = useRef<HTMLCanvasElement>(null);
  const weeklyChartRef = useRef<HTMLCanvasElement>(null);
  const weightWaistChartRef = useRef<HTMLCanvasElement>(null);
  const bpChartRef = useRef<HTMLCanvasElement>(null);

  // Inline CSS for faithful reproduction
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      body { font-family: Arial, sans-serif; margin: 20px; background: #f5f7fa; }
      .container { max-width: 1200px; margin: 0 auto; background: white; border-radius: 10px; padding: 20px; box-shadow: 0 5px 20px rgba(0,0,0,0.1); }
      .header { background: linear-gradient(135deg, #1e3c72, #2a5298); color: white; padding: 30px; text-align: center; border-radius: 10px; margin-bottom: 20px; }
      .upload-section { padding: 20px; background: #f8f9fa; border-radius: 8px; margin-bottom: 20px; }
      .file-input { width: 100%; padding: 15px; font-size: 16px; border: 2px dashed #ccc; border-radius: 8px; cursor: pointer; }
      .status { padding: 15px; margin: 15px 0; border-radius: 8px; text-align: center; }
      .status.success { background: #d4edda; color: #155724; }
      .status.error { background: #f8d7da; color: #721c24; }
      .hidden { display: none; }
      .report { padding: 20px; border: 1px solid #ddd; border-radius: 8px; margin-top: 20px; }
      table { width: 100%; border-collapse: collapse; margin: 20px 0; }
      th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
      th { background: #1e3c72; color: white; }
      .chart-container { margin: 30px 0; background: white; border: 1px solid #ddd; border-radius: 8px; padding: 20px; }
      .chart-wrapper { position: relative; height: 400px; margin: 20px 0; }
      .chart-title { font-size: 18px; font-weight: bold; color: #1e3c72; margin-bottom: 15px; text-align: center; }
      .weekly-summary { background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; margin-top: 30px; display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; }
      .summary-item { text-align: center; padding: 15px; background: white; border-radius: 6px; border-left: 4px solid #1e3c72; }
      .summary-label { font-weight: bold; color: #1e3c72; font-size: 12px; }
      .export-btn { background: #dc3545; color: white; border: none; padding: 12px 25px; margin: 10px; border-radius: 8px; cursor: pointer; }
      .report-header { display: none; }
      @media print { .report-header { display: block !important; } .web-data-period { display: none !important; } }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // File upload handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus({ message: `Reading file: ${file.name}`, type: 'success', visible: true });
    const reader = new FileReader();
    reader.onload = (evt) => {
      if (evt.target?.result) parseCSV(evt.target.result as string);
    };
    reader.readAsText(file);
  };

  // CSV parsing and processing
  const parseCSV = (csvText: string) => {
    Papa.parse(csvText, {
      header: true,
      complete: (results: any) => {
        setStatus({ message: `Found ${results.data.length} rows`, type: 'success', visible: true });
        processData(results.data);
      },
      error: () => {
        setStatus({ message: 'CSV parser error', type: 'error', visible: true });
      }
    });
  };

  // Faithful data processing: enrich, sort, organize by week/day/slot
  const processData = (data: any[]) => {
    // Enrich
   
    const enriched = data.filter(row => row['Final Time']).map(row => {
      const finalTime = new Date(row['Final Time']);
      
      // Validate the date
      if (isNaN(finalTime.getTime())) {
        console.warn('Invalid date found:', row['Final Time']);
        return null; // Skip invalid dates
      }
      
      // Enhanced exercise parsing - be more careful with the data
      let exerciseMinutes = null;
      let exerciseHR = null;
      let exerciseCalories = null;
      
      // Extract exercise minutes
      if (row['Exercise Duration (minutes)']) {
        const minutes = parseFloat(row['Exercise Duration (minutes)']);
        if (!isNaN(minutes) && minutes > 0) {
          exerciseMinutes = minutes;
        }
      } else if (row['Exercise Note']) {
        const match = String(row['Exercise Note']).match(/(\d+)\s*(min|mins|m)?/i);
        if (match) {
          const minutes = parseInt(match[1], 10);
          if (!isNaN(minutes) && minutes > 0) {
            exerciseMinutes = minutes;
          }
        }
      }
      
      // Extract HR - be more careful with parsing
      if (row['Average HR (bpm)']) {
        const hr = parseFloat(row['Average HR (bpm)']);
        if (!isNaN(hr) && hr > 0 && hr < 250) { // Reasonable HR range
          exerciseHR = hr;
        }
      }
      
      // Extract calories - be more careful with parsing
      if (row['Active Calories']) {
        const calories = parseFloat(row['Active Calories']);
        if (!isNaN(calories) && calories > 0) {
          exerciseCalories = calories;
        }
      }
      
      // Debug logging for exercise entries
      if (row['Entry Type'] === 'Exercise' || row['Exercise Type'] || exerciseMinutes || exerciseHR || exerciseCalories) {
        console.log('Processing exercise entry:', {
          entryType: row['Entry Type'],
          exerciseType: row['Exercise Type'],
          rawMinutes: row['Exercise Duration (minutes)'],
          rawHR: row['Average HR (bpm)'],
          rawCalories: row['Active Calories'],
          parsedMinutes: exerciseMinutes,
          parsedHR: exerciseHR,
          parsedCalories: exerciseCalories,
          finalTime: row['Final Time']
        });
      }
      
      const hour = finalTime.getHours();
      const timeSlot = getTimeSlot(hour);
      
      return {
        ...row,
        finalTime,
        dateStr: finalTime.toLocaleDateString(),
        timeStr: finalTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        dayOfWeek: finalTime.getDay(),
        hour: hour,
        entryType: row['Entry Type'] || '',
        readingType: row['Reading Type'] || '',
        sugarValue: row['Sugar Value'] ? parseFloat(row['Sugar Value']) : null,
        exerciseType: row['Exercise Type'] || '',
        exerciseNote: row['Exercise Note'] || '',
        exerciseMinutes,
        exerciseHR,
        exerciseCalories,
        // Also set these for compatibility with your existing code
        averageHR: exerciseHR,
        activeCalories: exerciseCalories,
        mealType: row['Meal Type'] || '',
        mealDescription: row['Meal Description'] || '',
        mealPhoto: row['Meal Photo'] || row['mealPhoto'] || '',
        notes: row['Notes'] || '',
        weight: row['Weight'] || '',
        waistSize: row['Waist Size'] || '',
        bloodPressure: row['Blood Pressure'] || '',
        hungerBefore: row['Before Hunger Scale'] || '',
        hungerAfter: row['After Hunger Scale'] || '',
        timeSlot: timeSlot,
        isCritical: row['Sugar Value'] ? parseFloat(row['Sugar Value']) > 250 : false,
        isHigh: row['Sugar Value'] ? parseFloat(row['Sugar Value']) > 180 : false
      };
    }).filter(entry => entry !== null).sort((a, b) => a.finalTime - b.finalTime);
    
    console.log(`Found ${enriched.filter(e => e.exerciseHR).length} entries with HR data`);
    console.log(`Found ${enriched.filter(e => e.exerciseCalories).length} entries with calorie data`);

    setAllData(enriched);
    setStatus({ message: `Processing ${enriched.length} valid entries...`, type: 'success', visible: true });
    // Organize by week
    const byWeek: any = {};
    enriched.forEach(entry => {
      const date = new Date(entry.finalTime);
      const dayOfWeek = date.getDay();
      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - dayOfWeek);
      startOfWeek.setHours(0, 0, 0, 0);
      const weekKey = startOfWeek.toDateString();
      if (!byWeek[weekKey]) {
        byWeek[weekKey] = {
          startDate: startOfWeek,
          days: []
        };
        for (let d = 0; d < 7; d++) {
          byWeek[weekKey].days[d] = [];
          for (let s = 0; s < 6; s++) {
            byWeek[weekKey].days[d][s] = [];
          }
        }
      }
      const dayIndex = entry.dayOfWeek;
      const slotIndex = entry.timeSlot;
      
      // Validate indices to prevent array out of bounds
      if (dayIndex >= 0 && dayIndex < 7 && slotIndex >= 0 && slotIndex < 6) {
        byWeek[weekKey].days[dayIndex][slotIndex].push(entry);
      } else {
        console.warn('Invalid day or time slot index:', { dayIndex, slotIndex, entry });
      }
    });
    setWeeklyData(byWeek);
    // Weeks sorted
    const weekArr = Object.values(byWeek).sort((a: any, b: any) => sortOrder === 'desc' ? b.startDate.getTime() - a.startDate.getTime() : a.startDate.getTime() - b.startDate.getTime());
    setWeeks(weekArr);
    setChartsReady(false);
    
    // Auto-generate filename based on data range
    const autoFilename = generateFilename(enriched);
    setExportFilename(autoFilename);
  };

  // Chart rendering (faithful)
  useEffect(() => {
    if (!chartsReady || allData.length === 0) return;
    // Glucose trend chart
    if (glucoseChartRef.current) {
      // Destroy previous chart instance if exists
      if ((glucoseChartRef.current as any)._chartInstance) {
        (glucoseChartRef.current as any)._chartInstance.destroy();
      }
      const chart = new Chart(glucoseChartRef.current, {
        type: 'line',
        data: getGlucoseTrendChartData(allData),
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: { y: { beginAtZero: false, min: 80, max: 300 } }
        }
      });
      (glucoseChartRef.current as any)._chartInstance = chart;
    }
    // Weekly averages chart
    if (weeklyChartRef.current) {
      // Destroy previous chart instance if exists
      if ((weeklyChartRef.current as any)._chartInstance) {
        (weeklyChartRef.current as any)._chartInstance.destroy();
      }
      // Always sort weeks oldest to newest for the chart
      const weeksOldestToNewest = [...weeks].sort((a, b) => a.startDate - b.startDate);
      const chart = new Chart(weeklyChartRef.current, {
        type: 'bar',
        data: getWeeklyAveragesChartData(weeksOldestToNewest),
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: { y: { beginAtZero: false, min: 70, max: 250 } }
        }
      });
      (weeklyChartRef.current as any)._chartInstance = chart;
    }
    // Weight/Waist chart
    if (weightWaistChartRef.current) {
      if ((weightWaistChartRef.current as any)._chartInstance) {
        (weightWaistChartRef.current as any)._chartInstance.destroy();
      }
      const { labels, weightData, waistData } = getDimensionsChartData(allData);
      const chart = new Chart(weightWaistChartRef.current, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Weight (lbs)',
              data: weightData,
              borderColor: '#1976d2',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              yAxisID: 'y',
              spanGaps: true,
            },
            {
              label: 'Waist (in)',
              data: waistData,
              borderColor: '#43a047',
              backgroundColor: 'rgba(67, 160, 71, 0.1)',
              yAxisID: 'y1',
              spanGaps: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: { legend: { position: 'top' } },
          scales: {
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: { display: true, text: 'Weight (lbs)' },
              min: 80,
              max: 300,
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              title: { display: true, text: 'Waist (in)' },
              min: 20,
              max: 60,
              grid: { drawOnChartArea: false },
            },
          },
        },
      });
      (weightWaistChartRef.current as any)._chartInstance = chart;
    }
    // BP chart
    if (bpChartRef.current) {
      if ((bpChartRef.current as any)._chartInstance) {
        (bpChartRef.current as any)._chartInstance.destroy();
      }
      const { labels, systolicData, diastolicData } = getDimensionsChartData(allData);
      const chart = new Chart(bpChartRef.current, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Systolic BP',
              data: systolicData,
              borderColor: '#d32f2f',
              backgroundColor: 'rgba(211, 47, 47, 0.1)',
              yAxisID: 'y',
              spanGaps: true,
            },
            {
              label: 'Diastolic BP',
              data: diastolicData,
              borderColor: '#fbc02d',
              backgroundColor: 'rgba(251, 192, 45, 0.1)',
              yAxisID: 'y',
              borderDash: [5, 5],
              spanGaps: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: { legend: { position: 'top' } },
          scales: {
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: { display: true, text: 'BP (mmHg)' },
              min: 40,
              max: 200,
            },
          },
        },
      });
      (bpChartRef.current as any)._chartInstance = chart;
    }
  }, [chartsReady, allData, weeks]);

  // When data changes, trigger chart rendering
  useEffect(() => {
    if (allData.length > 0 && weeks.length > 0) setChartsReady(true);
  }, [allData, weeks]);

  // Helper to identify random checks
  function isRandomCheck(readingType: string) {
    return readingType.toLowerCase().includes('random');
  }

  // Chart data helpers
  function getGlucoseTrendChartData(data: any[]) {
    const glucoseReadings = data.filter(d => d.entryType === 'Blood Sugar Reading' && d.sugarValue);
    const fastingData = glucoseReadings.filter(d => d.readingType.toLowerCase().includes('fasting'));
    const postMealData = glucoseReadings.filter(d => !d.readingType.toLowerCase().includes('fasting') && !isRandomCheck(d.readingType));
    // Unique dates
    const uniqueDates = Array.from(new Set(glucoseReadings.map(d => d.dateStr))).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const fastingValues = uniqueDates.map(date => {
      const reading = fastingData.find(d => d.dateStr === date);
      return reading ? reading.sugarValue : null;
    });
    const postMealValues = uniqueDates.map(date => {
      const reading = postMealData.find(d => d.dateStr === date);
      return reading ? reading.sugarValue : null;
    });
    return {
      labels: uniqueDates,
      datasets: [
        {
          label: 'Fasting Glucose',
          data: fastingValues,
          borderColor: '#2196F3',
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
          tension: 0.4,
          spanGaps: true
        },
        {
          label: 'Post-Meal Glucose',
          data: postMealValues,
          borderColor: '#FF6B6B',
          backgroundColor: 'rgba(255, 107, 107, 0.1)',
          tension: 0.4,
          spanGaps: true
        },
        {
          label: 'Fasting Target (130)',
          data: uniqueDates.map(() => 130),
          borderColor: '#4CAF50',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        {
          label: 'Post-Meal Target (180)',
          data: uniqueDates.map(() => 180),
          borderColor: '#FF9800',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        }
      ]
    };
  }
  function getWeeklyAveragesChartData(weeks: any[]) {
    const weekLabels = weeks.map(w => formatDate(w.startDate));
    const fastingAvgs = weeks.map(w => {
      const stats = calculateWeeklyStats(w);
      return stats.fasting.count > 0 ? stats.fasting.average : null;
    });
    const postMealAvgs = weeks.map(w => {
      const stats = calculateWeeklyStats(w);
      return stats.postMeal.count > 0 ? stats.postMeal.average : null;
    });
    return {
      labels: weekLabels,
      datasets: [
        {
          label: 'Fasting Average',
          data: fastingAvgs,
          backgroundColor: 'rgba(33, 150, 243, 0.6)',
          borderColor: '#2196F3',
          borderWidth: 1
        },
        {
          label: 'Post-Meal Average',
          data: postMealAvgs,
          backgroundColor: 'rgba(255, 107, 107, 0.6)',
          borderColor: '#FF6B6B',
          borderWidth: 1
        }
      ]
    };
  }

  // Weekly stats helpers (faithful)
  function calculateWeeklyStats(week: any) {
    const weekReadings: any[] = [];
    for (let d = 0; d < 7; d++) {
      for (let s = 0; s < 6; s++) {
        for (let e = 0; e < week.days[d][s].length; e++) {
          const entry = week.days[d][s][e];
          if (entry.entryType === 'Blood Sugar Reading' && entry.sugarValue) {
            weekReadings.push(entry);
          }
        }
      }
    }
    const fasting = weekReadings.filter(r => r.readingType.toLowerCase().includes('fasting'));
    const postMeal = weekReadings.filter(r => !r.readingType.toLowerCase().includes('fasting') && !isRandomCheck(r.readingType));
    return {
      fasting: {
        count: fasting.length,
        average: fasting.length > 0 ? Math.round(fasting.reduce((sum, r) => sum + r.sugarValue, 0) / fasting.length) : 0
      },
      postMeal: {
        count: postMeal.length,
        average: postMeal.length > 0 ? Math.round(postMeal.reduce((sum, r) => sum + r.sugarValue, 0) / postMeal.length) : 0
      }
    };
  }
  function getWeeklyVitals(week: any) {
    let weekEntries: any[] = [];
    for (let d = 0; d < 7; d++) {
      for (let s = 0; s < 6; s++) {
        weekEntries = weekEntries.concat(week.days[d][s]);
      }
    }
    const vitalsEntries = weekEntries.filter(entry => entry.weight || entry.waistSize || entry.bloodPressure).sort((a, b) => b.finalTime - a.finalTime);
    const vitals = { weight: null, waist: null, bp: null, date: null } as any;
    for (let i = 0; i < vitalsEntries.length; i++) {
      const entry = vitalsEntries[i];
      if (!vitals.weight && entry.weight) { vitals.weight = entry.weight; vitals.date = entry.dateStr; }
      if (!vitals.waist && entry.waistSize) { vitals.waist = entry.waistSize; if (!vitals.date) vitals.date = entry.dateStr; }
      if (!vitals.bp && entry.bloodPressure) { vitals.bp = entry.bloodPressure; if (!vitals.date) vitals.date = entry.dateStr; }
    }
    return vitals;
  }

  // Executive summary (faithful)
  function buildSummary(opts?: { boxesOnly?: boolean }) {
    const glucoseReadings = allData.filter(d => d.entryType === 'Blood Sugar Reading' && d.sugarValue);
    const fastingReadings = glucoseReadings.filter(d => d.readingType.toLowerCase().includes('fasting'));
    const postMealReadings = glucoseReadings.filter(d => !d.readingType.toLowerCase().includes('fasting') && !isRandomCheck(d.readingType));
    const randomReadings = glucoseReadings.filter(d => isRandomCheck(d.readingType));
    const fastingValues = fastingReadings.map(r => r.sugarValue);
    const postMealValues = postMealReadings.map(r => r.sugarValue);
    const fastingAvg = fastingValues.length > 0 ? Math.round(fastingValues.reduce((a, b) => a + b, 0) / fastingValues.length) : null;
    const postMealAvg = postMealValues.length > 0 ? Math.round(postMealValues.reduce((a, b) => a + b, 0) / postMealValues.length) : null;
    const fastingInTarget = fastingReadings.filter(r => r.sugarValue < 130).length;
    const postMealInTarget = postMealReadings.filter(r => r.sugarValue < 180).length;
    const totalInTarget = fastingInTarget + postMealInTarget;
    const structuredReadingsCount = fastingReadings.length + postMealReadings.length;
    const percentInTarget = structuredReadingsCount > 0 ? Math.round((totalInTarget / structuredReadingsCount) * 100) : null;
    const criticalReadings = glucoseReadings.filter(d => d.sugarValue > 250).length;
    let exerciseDays = 0;
    const exerciseEntries = allData.filter(d => d.entryType === 'Exercise' || d.exerciseType || d.exerciseNote);
    const uniqueExerciseDays: string[] = [];
    let totalExerciseMinutes = 0;
    let totalActiveCalories = 0;
    const exerciseTypeCount: Record<string, number> = {};
    for (let i = 0; i < exerciseEntries.length; i++) {
      const dateStr = exerciseEntries[i].dateStr;
      if (!uniqueExerciseDays.includes(dateStr)) {
        uniqueExerciseDays.push(dateStr);
        exerciseDays++;
      }
      if (exerciseEntries[i].exerciseMinutes) totalExerciseMinutes += exerciseEntries[i].exerciseMinutes;
      if (exerciseEntries[i].activeCalories) totalActiveCalories += exerciseEntries[i].activeCalories;
      if (exerciseEntries[i].exerciseType) {
        const type = exerciseEntries[i].exerciseType.trim().toLowerCase();
        if (type) exerciseTypeCount[type] = (exerciseTypeCount[type] || 0) + 1;
      }
    }
    // Most common exercise type
    let mostCommonType = '';
    if (Object.keys(exerciseTypeCount).length > 0) {
      mostCommonType = Object.entries(exerciseTypeCount).sort((a, b) => b[1] - a[1])[0][0];
      mostCommonType = mostCommonType.charAt(0).toUpperCase() + mostCommonType.slice(1);
    }
    // Weight change
    const weightEntries = allData.filter(d => d.weight);
    const weightStart = weightEntries.length > 0 ? weightEntries[0].weight : null;
    const weightEnd = weightEntries.length > 0 ? weightEntries[weightEntries.length - 1].weight : null;
    // BP change
    const bpEntries = allData.filter(d => d.bloodPressure);
    const bpStart = bpEntries.length > 0 ? bpEntries[0].bloodPressure : null;
    const bpEnd = bpEntries.length > 0 ? bpEntries[bpEntries.length - 1].bloodPressure : null;
    // Executive summary line
    let execSummary = 'Glucose control ';
    if (percentInTarget === null) execSummary += 'has no data.';
    else if (percentInTarget >= 80) execSummary += 'excellent';
    else if (percentInTarget >= 60) execSummary += 'good';
    else if (percentInTarget >= 40) execSummary += 'moderate';
    else execSummary += 'needs improvement';
    if (percentInTarget !== null) execSummary += `: ${percentInTarget}% in target`;
    if (criticalReadings > 0) execSummary += `, ${criticalReadings} critical reading${criticalReadings > 1 ? 's' : ''}`;
    if (!opts?.boxesOnly) {
      if (exerciseDays > 0) execSummary += `, ${exerciseDays} exercise day${exerciseDays > 1 ? 's' : ''}`;
      if (totalExerciseMinutes > 0) execSummary += `. Total exercise: ${totalExerciseMinutes} min`;
      if (totalActiveCalories > 0) execSummary += `. Total active calories: ${Math.round(totalActiveCalories)} kcal`;
      if (mostCommonType) execSummary += `. Most common: ${mostCommonType}`;
    }
    if (weightStart && weightEnd) execSummary += `. Weight: ${weightStart} → ${weightEnd} lbs`;
    if (bpStart && bpEnd) execSummary += `. BP: ${bpStart} → ${bpEnd}`;
    execSummary += '.';

    // Note: Footnote for random checks moved to individual weekly sections

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24, textAlign: 'left' }}>
        {fastingAvg !== null && (
          <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 18px', boxShadow: '0 1px 3px rgba(30,60,114,0.04)' }}><div style={{ fontSize: 13, color: '#1e3c72', fontWeight: 600 }}>Fasting Avg</div><div style={{ fontSize: 20, fontWeight: 'bold', color: '#1976d2' }}>{fastingAvg} mg/dL</div></div>
        )}
        {postMealAvg !== null && (
          <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 18px', boxShadow: '0 1px 3px rgba(30,60,114,0.04)' }}><div style={{ fontSize: 13, color: '#1e3c72', fontWeight: 600 }}>Post-Meal Avg</div><div style={{ fontSize: 20, fontWeight: 'bold', color: '#1976d2' }}>{postMealAvg} mg/dL</div></div>
        )}
        {percentInTarget !== null && (
          <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 18px', boxShadow: '0 1px 3px rgba(30,60,114,0.04)' }}><div style={{ fontSize: 13, color: '#1e3c72', fontWeight: 600 }}>% In Target</div><div style={{ fontSize: 20, fontWeight: 'bold', color: percentInTarget >= 80 ? '#388e3c' : percentInTarget >= 60 ? '#fbc02d' : '#d32f2f' }}>{percentInTarget}% {percentInTarget >= 80 ? '✔️' : percentInTarget >= 60 ? '⚠️' : '❗'}</div></div>
        )}
        {criticalReadings > 0 && (
          <div style={{ background: '#fff3e0', borderRadius: 8, padding: '12px 18px', boxShadow: '0 1px 3px rgba(30,60,114,0.04)' }}><div style={{ fontSize: 13, color: '#d32f2f', fontWeight: 600 }}>Critical</div><div style={{ fontSize: 20, fontWeight: 'bold', color: '#d32f2f' }}>{criticalReadings} ❗</div></div>
        )}
        {exerciseDays > 0 && (
          <div style={{ background: '#e8f5e9', borderRadius: 8, padding: '12px 18px', boxShadow: '0 1px 3px rgba(30,60,114,0.04)' }}>
            <div style={{ fontSize: 13, color: '#388e3c', fontWeight: 600 }}>Exercise Days</div>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#388e3c' }}>{exerciseDays}</div>
            {(totalExerciseMinutes > 0 || mostCommonType || totalActiveCalories > 0) && (
              <div style={{ fontSize: 13, color: '#388e3c', marginTop: 4 }}>
                {totalExerciseMinutes > 0 && <span>Total: {totalExerciseMinutes} min</span>}
                {totalActiveCalories > 0 && <span> &middot; {Math.round(totalActiveCalories)} kcal</span>}
                {(totalExerciseMinutes > 0 || totalActiveCalories > 0) && mostCommonType && <span> &middot; </span>}
                {mostCommonType && <span>Most: {mostCommonType}</span>}
              </div>
            )}
          </div>
        )}
        {weightStart && weightEnd && weightStart !== weightEnd && (
          <div style={{ background: '#e3f2fd', borderRadius: 8, padding: '12px 18px', boxShadow: '0 1px 3px rgba(30,60,114,0.04)' }}><div style={{ fontSize: 13, color: '#1976d2', fontWeight: 600 }}>Weight</div><div style={{ fontSize: 20, fontWeight: 'bold', color: '#1976d2' }}>{weightStart} → {weightEnd} lbs</div></div>
        )}
        {bpStart && bpEnd && bpStart !== bpEnd && (
          <div style={{ background: '#ede7f6', borderRadius: 8, padding: '12px 18px', boxShadow: '0 1px 3px rgba(30,60,114,0.04)' }}><div style={{ fontSize: 13, color: '#512da8', fontWeight: 600 }}>BP</div><div style={{ fontSize: 20, fontWeight: 'bold', color: '#512da8' }}>{bpStart} → {bpEnd}</div></div>
        )}
      </div>
    );
  }

  // Helper function to check if the note contains minutes info that's already parsed
function hasMinutesInNote(note: string, parsedMinutes: number | null): boolean {
  if (!note || !parsedMinutes) return false;
  
  // Check if the note contains the same minutes value that was parsed
  const match = String(note).match(/(\d+)\s*(min|mins|m)?/i);
  if (match && match[1]) {
    const noteMinutes = parseInt(match[1], 10);
    return noteMinutes === parsedMinutes;
  }
  
  return false;
}

  // Timeline grid cell rendering (faithful)
  function formatEvent(event: any) {
    // Blood Sugar Reading
    if (event.entryType === 'Blood Sugar Reading' && event.sugarValue) {
      const isRandom = isRandomCheck(event.readingType);
      let color, textColor;
      
      if (event.isCritical) {
        color = '#ff1744';
        textColor = 'white';
      } else if (event.isHigh) {
        color = '#ffebee';
        textColor = '#c62828';
      } else if (isRandom) {
        color = '#e0f2f1'; // Light teal for random checks
        textColor = '#00695c'; // Dark teal text that works with blood drop icon
      } else {
        color = '#e3f2fd'; // Default blue
        textColor = '#1565c0';
      }
      const borderColor = isRandom ? '#00695c' : '#1976d2';
      return (
        <div style={{ margin: '3px 0', padding: '4px 6px', borderRadius: 4, fontSize: 11, background: color, color: textColor, borderLeft: `3px solid ${borderColor}` }}>
          <div style={{ fontWeight: 'bold', fontSize: 10 }}>🩸 {event.timeStr}</div>
          <div>
            <strong>{event.sugarValue} mg/dL</strong><br />
            {event.readingType}
            {(event.mealType || event.mealDescription) && (
              <div style={{ fontSize: 10, marginTop: 2, color: '#6a1b9a' }}>
                <span role="img" aria-label="Meal">🍽️</span> {event.mealType ? `${event.mealType}: ` : ''}{event.mealDescription}
              </div>
            )}
          </div>
          {event.notes && (
            <div style={{ fontSize: 10, marginTop: 2, color: '#555' }}>
              <span role="img" aria-label="Note">📝</span> {event.notes}
            </div>
          )}
        </div>
      );
    }
    // Meal
    if (event.entryType === 'Meal' || event.mealType || event.mealDescription) {
      return (
        <div style={{ margin: '3px 0', padding: '4px 6px', borderRadius: 4, fontSize: 11, background: '#f3e5f5', color: '#6a1b9a', borderLeft: '3px solid #7b1fa2' }}>
          <div style={{ fontWeight: 'bold', fontSize: 10 }}>🍽️ {event.timeStr}</div>
          <div><strong>{event.mealType || 'Meal'}</strong>
            {event.mealDescription && (
              <div style={{ fontSize: 10, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                {event.mealDescription}
                {event.mealPhoto && (
                  <a
                    href={event.mealPhoto}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginLeft: 4, display: 'inline-flex', alignItems: 'center' }}
                    title="View meal photo"
                  >
                    <Camera style={{ width: 14, height: 14, color: '#1976d2', verticalAlign: 'middle' }} />
                  </a>
                )}
              </div>
            )}
            {(event.hungerBefore || event.hungerAfter) && <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>Hunger: {event.hungerBefore || '?'} → {event.hungerAfter || '?'}</div>}
          </div>
          {event.notes && (
            <div style={{ fontSize: 10, marginTop: 2, color: '#555' }}>
              <span role="img" aria-label="Note">📝</span> {event.notes}
            </div>
          )}
        </div>
      );
    }
    
  // Exercise
if (event.entryType === 'Exercise' || event.exerciseType || event.exerciseNote || event.exerciseMinutes) {
  return (
    <div style={{ margin: '3px 0', padding: '4px 6px', borderRadius: 4, fontSize: 11, background: '#e8f5e8', color: '#2e7d32', borderLeft: '3px solid #388e3c' }}>
      <div style={{ fontWeight: 'bold', fontSize: 10 }}>🏃 {event.timeStr}</div>
      <div>
        <strong>{event.exerciseType || 'Exercise'}</strong>
        
        {/* Only show exercise note if it doesn't contain the minutes that are already parsed */}
        {event.exerciseNote && !hasMinutesInNote(event.exerciseNote, event.exerciseMinutes) && (
          <div style={{ fontSize: 10, marginTop: 2 }}>{event.exerciseNote}</div>
        )}
        
        {/* Show metrics if any exist */}
        {(event.exerciseMinutes || event.exerciseHR || event.exerciseCalories) && (
          <div style={{ 
            fontSize: 9, 
            marginTop: 3, 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '3px',
            alignItems: 'center'
          }}>
            {event.exerciseMinutes && (
              <span style={{ 
                background: '#dcfce7', 
                color: '#166534', 
                padding: '1px 3px', 
                borderRadius: 2,
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}>
                ⏱️{event.exerciseMinutes}min
              </span>
            )}
            
            {event.exerciseHR && (
              <span style={{ 
                background: event.exerciseHR > 120 ? '#fef2f2' : '#f0fdf4',
                color: event.exerciseHR > 120 ? '#dc2626' : '#166534',
                padding: '1px 3px', 
                borderRadius: 2,
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}>
                ❤️{Math.round(event.exerciseHR)}
              </span>
            )}
            
            {event.exerciseCalories && (
              <span style={{ 
                background: '#fff7ed', 
                color: '#c2410c', 
                padding: '1px 3px', 
                borderRadius: 2,
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}>
                🔥{Math.round(event.exerciseCalories)}
              </span>
            )}
          </div>
        )}
      </div>
      
      {event.notes && (
        <div style={{ fontSize: 10, marginTop: 2, color: '#555' }}>
          📝 {event.notes}
        </div>
      )}
    </div>
  );
}

    // Notes (medication, etc.)
    if (event.notes && !event.mealDescription && !event.exerciseNote) {
      const isMedication = event.notes.toLowerCase().includes('medicine') || event.notes.toLowerCase().includes('medication') || event.notes.toLowerCase().includes('forgot');
      const bgColor = isMedication ? '#fff3e0' : '#fafafa';
      const textColor = isMedication ? '#ef6c00' : '#555';
      const icon = isMedication ? '💊' : '📝';
      return (
        <div style={{ margin: '3px 0', padding: '4px 6px', borderRadius: 4, fontSize: 11, background: bgColor, color: textColor, borderLeft: '3px solid #666' }}>
          <div style={{ fontWeight: 'bold', fontSize: 10 }}>{icon} {event.timeStr}</div>
          <div>{event.notes}</div>
        </div>
      );
    }
    // For any other event, still show notes if present
    if (event.notes) {
      return (
        <div style={{ margin: '3px 0', padding: '4px 6px', borderRadius: 4, fontSize: 11, background: '#fafafa', color: '#555', borderLeft: '3px solid #666' }}>
          <div style={{ fontWeight: 'bold', fontSize: 10 }}>📝 {event.timeStr}</div>
          <div>{event.notes}</div>
        </div>
      );
    }
    return null;
  }

  function buildWeeklyGridWithAnalysis(week: any, weekIndex: number) {
    const weekStart = formatDate(week.startDate);
    const weekEnd = formatDate(new Date(week.startDate.getTime() + 6 * 24 * 60 * 60 * 1000));
    const stats = { ...calculateWeeklyStats(week), vitals: getWeeklyVitals(week) };
    
    // Check for random checks in this week
    const weekRandomChecks: any[] = [];
    for (let d = 0; d < 7; d++) {
      for (let s = 0; s < 6; s++) {
        for (const entry of week.days[d][s]) {
          if (entry.entryType === 'Blood Sugar Reading' && entry.sugarValue && isRandomCheck(entry.readingType)) {
            weekRandomChecks.push(entry);
          }
        }
      }
    }
  
    // Enhanced exercise calculation for this week
    let weekExerciseData: {
      totalMinutes: number;
      sessionCount: number;
      avgHr: number | null;
      totalCalories: number | null;
    } = {
      totalMinutes: 0,
      sessionCount: 0,
      avgHr: null,
      totalCalories: null
    };
  
    const exerciseEntries: any[] = [];
    for (let d = 0; d < 7; d++) {
      for (let s = 0; s < 6; s++) {
        for (const entry of week.days[d][s]) {
          if ((entry.entryType === 'Exercise' || entry.exerciseType) && entry.exerciseMinutes) {
            exerciseEntries.push(entry);
            weekExerciseData.totalMinutes += entry.exerciseMinutes;
          }
        }
      }
    }
  
    if (exerciseEntries.length > 0) {
      weekExerciseData.sessionCount = exerciseEntries.length;
      
      // Calculate average HR
      const hrValues = exerciseEntries.map(e => e.exerciseHR || e.averageHR).filter((hr): hr is number => hr !== null && hr !== undefined);
      if (hrValues.length > 0) {
        weekExerciseData.avgHr = Math.round(hrValues.reduce((a, b) => a + b, 0) / hrValues.length);
      }
      
      // Calculate total calories
      const calorieValues = exerciseEntries.map(e => e.exerciseCalories || e.activeCalories).filter((cal): cal is number => cal !== null && cal !== undefined);
      if (calorieValues.length > 0) {
        weekExerciseData.totalCalories = Math.round(calorieValues.reduce((a, b) => a + b, 0));
      }
    }
  
    return (
      <div key={weekIndex} style={{ marginBottom: 40, pageBreakInside: 'avoid' }}>
        <h2 style={{ color: '#1e3c72' }}>{weekStart} – {weekEnd}</h2>
        
        {/* Enhanced analysis box */}
        <div style={{ background: '#f0f8ff', border: '1px solid #b3d9ff', borderRadius: 8, padding: 20, margin: '15px 0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 25 }}>
            
            {/* Glucose Control */}
            <div>
              <h5 style={{ margin: 0, marginBottom: 10, color: '#2a5298', borderBottom: '1px solid #ddd', paddingBottom: 5 }}>🩸 Glucose Control</h5>
              {(stats.fasting.count > 0 || stats.postMeal.count > 0) ? (
                <div style={{ fontSize: 13 }}>
                  {stats.fasting.count > 0 && <div style={{ marginBottom: 5 }}><strong>Fasting:</strong> {stats.fasting.average} mg/dL ({stats.fasting.count})</div>}
                  {stats.postMeal.count > 0 && <div><strong>Post-Meal:</strong> {stats.postMeal.average} mg/dL ({stats.postMeal.count})</div>}
                </div>
              ) : <div style={{ color: '#888', fontSize: 13 }}>No glucose readings</div>}
            </div>
            
            {/* Enhanced Vitals & Exercise */}
            <div>
              <h5 style={{ margin: 0, marginBottom: 10, color: '#2a5298', borderBottom: '1px solid #ddd', paddingBottom: 5 }}>
                📏 Vitals & Exercise{stats.vitals.date ? ` (${stats.vitals.date})` : ''}
              </h5>
              <div style={{ fontSize: 13 }}>
                {stats.vitals.weight && <div style={{ marginBottom: 5 }}><strong>Weight:</strong> {stats.vitals.weight} lbs</div>}
                {stats.vitals.waist && <div style={{ marginBottom: 5 }}><strong>Waist:</strong> {stats.vitals.waist}"</div>}
                {stats.vitals.bp && <div style={{ marginBottom: 5 }}><strong>BP:</strong> {stats.vitals.bp}</div>}
                
                {/* Enhanced Exercise summary */}
                {weekExerciseData.totalMinutes > 0 ? (
                  <>
                    <div style={{ marginBottom: 5 }}>
                      <strong>Exercise:</strong> {weekExerciseData.totalMinutes} min ({weekExerciseData.sessionCount} sessions)
                    </div>
                    {(weekExerciseData.avgHr || weekExerciseData.totalCalories) && (
                      <div style={{ 
                        fontSize: 11, 
                        color: '#666',
                        background: '#f8f9fa',
                        padding: '2px 4px',
                        borderRadius: 3,
                        display: 'inline-block',
                        marginTop: 2
                      }}>
                        {weekExerciseData.avgHr && `Avg HR: ${weekExerciseData.avgHr} bpm`}
                        {weekExerciseData.avgHr && weekExerciseData.totalCalories && ' • '}
                        {weekExerciseData.totalCalories && `${weekExerciseData.totalCalories} cal`}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ color: '#888', fontSize: 13 }}>No exercise data</div>
                )}
              </div>
            </div>
            
            {/* Key Insights */}
            <div>
              <h5 style={{ margin: 0, marginBottom: 10, color: '#2a5298', borderBottom: '1px solid #ddd', paddingBottom: 5 }}>💡 Key Insights</h5>
              <div style={{ fontSize: 13 }}>
                {(() => {
                  const insights: string[] = [];
                  if (stats.fasting.count > 0) {
                    if (stats.fasting.average <= 130) insights.push('✅ Fasting in target');
                    else insights.push('⚠️ Fasting above target');
                  }
                  if (stats.postMeal.count > 0) {
                    if (stats.postMeal.average < 180) insights.push('✅ Post-meal in target');
                    else insights.push('⚠️ Post-meal above target');
                  }
                  const totalReadings = stats.fasting.count + stats.postMeal.count + weekRandomChecks.length;
                  if (totalReadings > 0) insights.push(`📊 ${totalReadings} total readings`);
                  
                  // Exercise insights
                  if (weekExerciseData.totalMinutes > 0) {
                    if (weekExerciseData.totalMinutes >= 150) insights.push('💪 Great exercise week!');
                    else if (weekExerciseData.totalMinutes >= 75) insights.push('👍 Good exercise activity');
                  }
                  
                  if (insights.length > 0) return insights.map((ins, i) => <div key={i} style={{ marginBottom: 3 }}>{ins}</div>);
                  return <div style={{ color: '#888' }}>No data to analyze</div>;
                })()}
              </div>
            </div>
          </div>
          
          {/* Random checks footnote for this week */}
          {weekRandomChecks.length > 0 && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 12, fontStyle: 'italic', borderTop: '1px solid #ddd', paddingTop: 8 }}>
              *This week includes {weekRandomChecks.length} random check{weekRandomChecks.length > 1 ? 's' : ''} ({weekRandomChecks.map(r => `${r.sugarValue} mg/dL at ${r.timeStr}`).join(', ')}) excluded from averages
            </div>
          )}
        </div>
        
        {/* Weekly grid table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ width: 120, background: '#1e3c72', color: 'white', padding: '12px 8px' }}>TIME SLOT</th>
              {dayNames.map((d, i) => (
                <th key={i} style={{ background: '#1e3c72', color: 'white', padding: '12px 8px' }}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((slot, s) => (
              <tr key={s}>
                <td style={{ background: '#2a5298', color: 'white', padding: '12px 8px', textAlign: 'center', fontWeight: 'bold' }}>
                  {slot.split('\n').map((line, i) => <div key={i}>{line}</div>)}
                </td>
                {Array.from({ length: 7 }).map((_, d) => (
                  <td key={d} style={{ border: '1px solid #ddd', padding: 8, minHeight: 80, verticalAlign: 'top', background: week.days[d][s].length === 0 ? '#fafafa' : undefined }}>
                    {week.days[d][s].map((event: any, i: number) => (
                      <React.Fragment key={i}>{formatEvent(event)}</React.Fragment>
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // --- Export helpers ---
  // Convert charts to images for export
  function replaceChartsWithImages() {
    const chartCanvases = document.querySelectorAll('.chart-container canvas');
    const sparklineCanvases = document.querySelectorAll('.container .recent-sparkline canvas, .container > div[style*="flex"] canvas');
    const replaced: { canvas: HTMLCanvasElement; img: HTMLImageElement }[] = [];
    chartCanvases.forEach((canvas: any) => {
      const img = document.createElement('img');
      img.src = canvas.toDataURL('image/png');
      img.width = canvas.width;
      img.height = canvas.height;
      img.className = canvas.className;
      img.style.cssText = canvas.style.cssText;
      canvas.parentNode.insertBefore(img, canvas);
      canvas.style.display = 'none';
      replaced.push({ canvas, img });
    });
    // Also replace sparklines
    sparklineCanvases.forEach((canvas: any) => {
      if (canvas.style.display === 'none') return; // already replaced
      const img = document.createElement('img');
      img.src = canvas.toDataURL('image/png');
      img.width = canvas.width;
      img.height = canvas.height;
      img.className = canvas.className;
      img.style.cssText = canvas.style.cssText;
      canvas.parentNode.insertBefore(img, canvas);
      canvas.style.display = 'none';
      replaced.push({ canvas, img });
    });
    return replaced;
  }
  function restoreChartsFromImages(replaced: { canvas: HTMLCanvasElement; img: HTMLImageElement }[]) {
    replaced.forEach(pair => {
      if (pair.img.parentNode) pair.img.parentNode.removeChild(pair.img);
      pair.canvas.style.display = '';
    });
  }

  // PDF export
  function handleExportPDF() {
    // Convert charts to images
    const replacedCharts = replaceChartsWithImages();
    // Store original title and set custom filename
    const originalTitle = document.title;
    document.title = exportFilename;
    // Hide elements for print
    const selectors = [
      '.upload-section',
      '.status',
      '.export-btn',
      '.header',
      '#sortOrder',
      '.web-data-period',
      '.sort-label',
      '#exportFilename',
      '#exportFilename2',
      'label[for="exportFilename"]',
      'label[for="exportFilename2"]'
    ];
    const hiddenElements: { element: HTMLElement; originalDisplay: string }[] = [];
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        hiddenElements.push({ element: el as HTMLElement, originalDisplay: (el as HTMLElement).style.display });
        (el as HTMLElement).style.display = 'none';
      });
    });
    // Show report header
    const reportHeaders = document.getElementsByClassName('report-header');
    Array.from(reportHeaders).forEach(el => {
      (el as HTMLElement).style.display = 'block';
    });
    // Wait for images to render before printing
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        // Restore after print
        setTimeout(() => {
          // Restore original title
          document.title = originalTitle;
          hiddenElements.forEach(item => {
            item.element.style.display = item.originalDisplay;
          });
          Array.from(reportHeaders).forEach(el => {
            (el as HTMLElement).style.display = 'none';
          });
          restoreChartsFromImages(replacedCharts);
        }, 1000);
      });
    });
  }

  // HTML export
  function handleExportHTML() {
    setIsExporting(true);
    // Convert charts to images
    const replacedCharts = replaceChartsWithImages();
    // Hide elements for export
    const selectors = [
      '.upload-section',
      '.status',
      '.export-btn',
      '.header',
      '#sortOrder',
      '.web-data-period',
      '.sort-label',
      '#exportFilename',
      '#exportFilename2',
      'label[for="exportFilename"]',
      'label[for="exportFilename2"]'
    ];
    const hiddenElements: { element: HTMLElement; originalDisplay: string }[] = [];
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        hiddenElements.push({ element: el as HTMLElement, originalDisplay: (el as HTMLElement).style.display });
        (el as HTMLElement).style.display = 'none';
      });
    });
    // Show report header
    const reportHeaders = document.getElementsByClassName('report-header');
    Array.from(reportHeaders).forEach(el => {
      (el as HTMLElement).style.display = 'block';
    });
    setTimeout(() => {
      // Get styles
      let styles = '';
      document.querySelectorAll('style').forEach(styleTag => {
        styles += styleTag.outerHTML;
      });
      document.querySelectorAll('link[rel="stylesheet"]').forEach(linkTag => {
        styles += linkTag.outerHTML;
      });
      // Get report HTML
      const container = document.querySelector('.container');
      if (!container) return;
      const htmlContent = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Diabetes & Lifestyle Weekly Log Report</title><meta name="viewport" content="width=device-width, initial-scale=1.0">${styles}</head><body>${container.outerHTML}</body></html>`;
      // Download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportFilename}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Restore
      Array.from(reportHeaders).forEach(el => {
        (el as HTMLElement).style.display = 'none';
      });
      hiddenElements.forEach(item => {
        item.element.style.display = item.originalDisplay;
      });
      restoreChartsFromImages(replacedCharts);
      setIsExporting(false);
    }, 100);
  }

  // --- END Export helpers ---

  // Sort order change
  function handleSortOrder(e: React.ChangeEvent<HTMLSelectElement>) {
    setSortOrder(e.target.value as 'desc' | 'asc');
    // Do not call processData here; let useEffect handle it
  }

  // Re-process data when sortOrder or allData changes
  useEffect(() => {
    if (allData.length > 0) {
      processData(allData);
    }
    // eslint-disable-next-line
  }, [sortOrder]);

  // Data period
  const dataPeriod = allData.length > 0 ? `${allData[0].dateStr} – ${allData[allData.length - 1].dateStr}` : '';

  // Helper to parse BP and aggregate dimensions by week
  function getDimensionsChartData(data: any[]) {
    // Group by week start date, keep latest value for each metric per week
    const byWeek: Record<string, { weekStart: Date; weight?: number; waist?: number; systolic?: number; diastolic?: number }> = {};
    data.forEach(row => {
      const date = new Date(row.finalTime);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      weekStart.setHours(0, 0, 0, 0);
      const weekKey = weekStart.toISOString();
      if (!byWeek[weekKey]) byWeek[weekKey] = { weekStart };
      // Always use the latest value in the week (since data is sorted by date ascending)
      if (row.weight && !isNaN(parseFloat(row.weight))) byWeek[weekKey].weight = parseFloat(row.weight);
      if (row.waistSize && !isNaN(parseFloat(row.waistSize))) byWeek[weekKey].waist = parseFloat(row.waistSize);
      if (row.bloodPressure) {
        const match = String(row.bloodPressure).match(/(\d+)[^\d]+(\d+)/);
        if (match) {
          byWeek[weekKey].systolic = parseInt(match[1], 10);
          byWeek[weekKey].diastolic = parseInt(match[2], 10);
        }
      }
    });
    const weekKeys = Object.keys(byWeek).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const labels = weekKeys.map(k => byWeek[k].weekStart.toLocaleDateString());
    const weightData = weekKeys.map(k => byWeek[k].weight ?? null);
    const waistData = weekKeys.map(k => byWeek[k].waist ?? null);
    const systolicData = weekKeys.map(k => byWeek[k].systolic ?? null);
    const diastolicData = weekKeys.map(k => byWeek[k].diastolic ?? null);
    return { labels, weightData, waistData, systolicData, diastolicData };
  }

  // --- Recent Trends (sparklines) ---
  const fastingSparkRef = useRef<HTMLCanvasElement>(null);
  const postMealSparkRef = useRef<HTMLCanvasElement>(null);
  const weightSparkRef = useRef<HTMLCanvasElement>(null);
  const waistSparkRef = useRef<HTMLCanvasElement>(null);
  const bpSparkRef = useRef<HTMLCanvasElement>(null);
  const exerciseSparkRef = useRef<HTMLCanvasElement>(null);

  // Helper: get last 4-5 weeks (1 month) of data
  function getRecentWeeks(weeks: any[], count = 5) {
    return weeks.slice(0, count).sort((a, b) => a.startDate - b.startDate);
  }

  // Helper: get sparkline data for each metric
  function getSparklineData(weeks: any[], metric: 'fasting' | 'postMeal' | 'weight' | 'waist' | 'bp') {
    if (metric === 'fasting' || metric === 'postMeal') {
      return weeks.map(w => {
        const stats = calculateWeeklyStats(w);
        return metric === 'fasting' ? (stats.fasting.count > 0 ? stats.fasting.average : null) : (stats.postMeal.count > 0 ? stats.postMeal.average : null);
      });
    }
    if (metric === 'weight' || metric === 'waist' || metric === 'bp') {
      return weeks.map(w => {
        const vitals = getWeeklyVitals(w);
        if (metric === 'weight') return vitals.weight ? parseFloat(vitals.weight) : null;
        if (metric === 'waist') return vitals.waist ? parseFloat(vitals.waist) : null;
        if (metric === 'bp') {
          if (vitals.bp) {
            const match = String(vitals.bp).match(/(\d+)[^\d]+(\d+)/);
            if (match) return { systolic: parseInt(match[1], 10), diastolic: parseInt(match[2], 10) };
          }
          return null;
        }
        return null;
      });
    }
    return [];
  }

  // Helper: get exercise minutes per week
  function getExerciseMinutesPerWeek(weeks: any[]) {
    return weeks.map(week => {
      let total = 0;
      for (let d = 0; d < 7; d++) {
        for (let s = 0; s < 6; s++) {
          for (const entry of week.days[d][s]) {
            if ((entry.entryType === 'Exercise' || entry.exerciseType) && entry.exerciseMinutes) {
              total += entry.exerciseMinutes;
            }
          }
        }
      }
      return total;
    });
  }

  // Render sparklines when chartsReady changes
  useEffect(() => {
    if (!chartsReady || weeks.length === 0) return;
    const recentWeeks = getRecentWeeks(weeks, 5);
    // Fasting
    if (fastingSparkRef.current) {
      if ((fastingSparkRef.current as any)._chartInstance) (fastingSparkRef.current as any)._chartInstance.destroy();
      const data = getSparklineData(recentWeeks, 'fasting');
      const chart = new Chart(fastingSparkRef.current, {
        type: 'line',
        data: { labels: recentWeeks.map(w => formatDate(w.startDate).slice(0,5)), datasets: [{ data, borderColor: '#1976d2', backgroundColor: 'rgba(25,118,210,0.08)', pointRadius: data.map((v, i) => (i === 0 || i === data.length-1) ? 5 : 2), pointBackgroundColor: data.map((v, i) => (i === 0 || i === data.length-1) ? '#1976d2' : '#1976d2'), tension: 0.4, fill: false }] },
        options: { plugins: { legend: { display: false } }, scales: { x: { display: false }, y: { display: false } }, elements: { line: { borderWidth: 2 } }, responsive: false, maintainAspectRatio: false }
      });
      (fastingSparkRef.current as any)._chartInstance = chart;
    }
    // Post-meal
    if (postMealSparkRef.current) {
      if ((postMealSparkRef.current as any)._chartInstance) (postMealSparkRef.current as any)._chartInstance.destroy();
      const data = getSparklineData(recentWeeks, 'postMeal');
      const chart = new Chart(postMealSparkRef.current, {
        type: 'line',
        data: { labels: recentWeeks.map(w => formatDate(w.startDate).slice(0,5)), datasets: [{ data, borderColor: '#ff6b6b', backgroundColor: 'rgba(255,107,107,0.08)', pointRadius: data.map((v, i) => (i === 0 || i === data.length-1) ? 5 : 2), pointBackgroundColor: data.map((v, i) => (i === 0 || i === data.length-1) ? '#ff6b6b' : '#ff6b6b'), tension: 0.4, fill: false }] },
        options: { plugins: { legend: { display: false } }, scales: { x: { display: false }, y: { display: false } }, elements: { line: { borderWidth: 2 } }, responsive: false, maintainAspectRatio: false }
      });
      (postMealSparkRef.current as any)._chartInstance = chart;
    }
    // Weight
    if (weightSparkRef.current) {
      if ((weightSparkRef.current as any)._chartInstance) (weightSparkRef.current as any)._chartInstance.destroy();
      const data = getSparklineData(recentWeeks, 'weight');
      const chart = new Chart(weightSparkRef.current, {
        type: 'line',
        data: { labels: recentWeeks.map(w => formatDate(w.startDate).slice(0,5)), datasets: [{ data, borderColor: '#388e3c', backgroundColor: 'rgba(56,142,60,0.08)', pointRadius: data.map((v, i) => (i === 0 || i === data.length-1) ? 5 : 2), pointBackgroundColor: data.map((v, i) => (i === 0 || i === data.length-1) ? '#388e3c' : '#388e3c'), tension: 0.4, fill: false }] },
        options: { plugins: { legend: { display: false } }, scales: { x: { display: false }, y: { display: false } }, elements: { line: { borderWidth: 2 } }, responsive: false, maintainAspectRatio: false }
      });
      (weightSparkRef.current as any)._chartInstance = chart;
    }
    // Waist
    if (waistSparkRef.current) {
      if ((waistSparkRef.current as any)._chartInstance) (waistSparkRef.current as any)._chartInstance.destroy();
      const data = getSparklineData(recentWeeks, 'waist');
      const chart = new Chart(waistSparkRef.current, {
        type: 'line',
        data: { labels: recentWeeks.map(w => formatDate(w.startDate).slice(0,5)), datasets: [{ data, borderColor: '#fbc02d', backgroundColor: 'rgba(251,192,45,0.08)', pointRadius: data.map((v, i) => (i === 0 || i === data.length-1) ? 5 : 2), pointBackgroundColor: data.map((v, i) => (i === 0 || i === data.length-1) ? '#fbc02d' : '#fbc02d'), tension: 0.4, fill: false }] },
        options: { plugins: { legend: { display: false } }, scales: { x: { display: false }, y: { display: false } }, elements: { line: { borderWidth: 2 } }, responsive: false, maintainAspectRatio: false }
      });
      (waistSparkRef.current as any)._chartInstance = chart;
    }
    // BP
    if (bpSparkRef.current) {
      if ((bpSparkRef.current as any)._chartInstance) (bpSparkRef.current as any)._chartInstance.destroy();
      const bpData = getSparklineData(recentWeeks, 'bp');
      const systolic = bpData.map((v: any) => v ? v.systolic : null);
      const diastolic = bpData.map((v: any) => v ? v.diastolic : null);
      const chart = new Chart(bpSparkRef.current, {
        type: 'line',
        data: { labels: recentWeeks.map(w => formatDate(w.startDate).slice(0,5)), datasets: [
          { data: systolic, borderColor: '#d32f2f', backgroundColor: 'rgba(211,47,47,0.08)', pointRadius: systolic.map((v, i) => (i === 0 || i === systolic.length-1) ? 5 : 2), pointBackgroundColor: systolic.map((v, i) => (i === 0 || i === systolic.length-1) ? '#d32f2f' : '#d32f2f'), tension: 0.4, fill: false },
          { data: diastolic, borderColor: '#1976d2', backgroundColor: 'rgba(25,118,210,0.08)', pointRadius: diastolic.map((v, i) => (i === 0 || i === diastolic.length-1) ? 5 : 2), pointBackgroundColor: diastolic.map((v, i) => (i === 0 || i === diastolic.length-1) ? '#1976d2' : '#1976d2'), tension: 0.4, fill: false }
        ] },
        options: { plugins: { legend: { display: false } }, scales: { x: { display: false }, y: { display: false } }, elements: { line: { borderWidth: 2 } }, responsive: false, maintainAspectRatio: false }
      });
      (bpSparkRef.current as any)._chartInstance = chart;
    }
    // Exercise
    if (exerciseSparkRef.current) {
      if ((exerciseSparkRef.current as any)._chartInstance) (exerciseSparkRef.current as any)._chartInstance.destroy();
      const data = getExerciseMinutesPerWeek(recentWeeks);
      const chart = new Chart(exerciseSparkRef.current, {
        type: 'line',
        data: { labels: recentWeeks.map(w => formatDate(w.startDate).slice(0,5)), datasets: [{ data, borderColor: '#8e24aa', backgroundColor: 'rgba(142,36,170,0.08)', pointRadius: data.map((v, i) => (i === 0 || i === data.length-1) ? 5 : 2), pointBackgroundColor: data.map((v, i) => (i === 0 || i === data.length-1) ? '#8e24aa' : '#8e24aa'), tension: 0.4, fill: false }] },
        options: { plugins: { legend: { display: false } }, scales: { x: { display: false }, y: { display: false } }, elements: { line: { borderWidth: 2 } }, responsive: false, maintainAspectRatio: false }
      });
      (exerciseSparkRef.current as any)._chartInstance = chart;
    }
  }, [chartsReady, weeks]);



  return (
    <div className="container">
      <div className="header">
        <h1>📅 Chronological Diabetes & Lifestyle Weekly Log</h1>
        <p>Timeline-based medical log following natural daily rhythm</p>
      </div>
      <div className="upload-section">
        <input
          type="file"
          ref={fileInputRef}
          className="file-input"
          accept=".csv"
          onChange={handleFileChange}
        />
        {status.visible && (
          <div className={`status ${status.type}`}>{status.message}</div>
        )}
      </div>
      {allData.length > 0 && (
        <>
          <div style={{ textAlign: 'right', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label htmlFor="exportFilename" style={{ fontWeight: 'bold', color: '#1e3c72', fontSize: 14 }}>Filename:</label>
              <input
                id="exportFilename"
                type="text"
                value={exportFilename}
                onChange={(e) => setExportFilename(e.target.value)}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', minWidth: 200 }}
                placeholder="Enter filename"
              />
            </div>
            <button className="export-btn" onClick={handleExportPDF}>Download PDF</button>
            <button className="export-btn" onClick={handleExportHTML}>Download HTML</button>
          </div>
          <div style={{ textAlign: 'right', marginBottom: 10 }}>
            <label htmlFor="sortOrder" className="sort-label" style={{ fontWeight: 'bold', color: '#1e3c72' }}>Sort:</label>
            <select id="sortOrder" value={sortOrder} onChange={handleSortOrder} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', marginLeft: 8 }}>
              <option value="desc">Latest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
          <div className="web-data-period" style={{ fontWeight: 600, color: '#1e3c72', fontSize: 16, margin: '10px 0 0 0', letterSpacing: 0.2, textAlign: 'left' }}>Data Period: {dataPeriod}</div>
          {/* Report header for export/print only */}
          <div className="report-header" style={{ display: 'none' }}>
            <h1 style={{ color: '#1e3c72', fontSize: '2rem', marginBottom: '0.2em' }}>WEEKLY DIABETES & LIFESTYLE LOG</h1>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1976d2', marginBottom: '0.5em', display: 'flex', alignItems: 'center', gap: '1.5em' }}>
              <span>Data Period: {dataPeriod}</span>
              <span style={{ fontSize: '0.95rem', color: '#888', fontWeight: 400 }}>Generated: {new Date().toLocaleDateString()}</span>
            </div>
          </div>
          {/* Only show the summary boxes, not the text summary */}
          {buildSummary({ boxesOnly: true })}
          {/* Section label for sparklines */}
          <div style={{ fontWeight: 600, color: '#1e3c72', fontSize: 16, margin: '10px 0 0 0', letterSpacing: 0.2 }}>Recent Trends (Last 1 Month)</div>
          {/* Recent Trends Sparklines */}
          <div style={{ display: 'flex', gap: 18, margin: '18px 0 32px 0', flexWrap: 'wrap', justifyContent: 'center', overflow: 'visible', position: 'relative' }}>
            {/* Fasting */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, overflow: 'visible', position: 'relative' }}>
              <span style={{ fontSize: 11, color: '#1976d2', fontWeight: 600, marginBottom: 2 }}>Fasting</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'visible' }}>
                <span style={{ fontSize: 11, color: '#1976d2', minWidth: 28, textAlign: 'right' }}>{(() => { 
                  const data = getSparklineData(getRecentWeeks(weeks, 5), 'fasting'); 
                  const val = data[0];
                  return (typeof val === 'number' || typeof val === 'string') ? val : '';
                })()}</span>
                <canvas ref={fastingSparkRef} width={110} height={44} style={{ maxWidth: 120, maxHeight: 44, overflow: 'visible', position: 'relative', zIndex: 2 }} />
                <span style={{ fontSize: 11, color: '#1976d2', minWidth: 28, textAlign: 'left' }}>{(() => { 
                  const data = getSparklineData(getRecentWeeks(weeks, 5), 'fasting'); 
                  const val = data[data.length-1];
                  return (typeof val === 'number' || typeof val === 'string') ? val : '';
                })()}</span>
              </div>
            </div>
            {/* Post-Meal */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, overflow: 'visible', position: 'relative' }}>
              <span style={{ fontSize: 11, color: '#ff6b6b', fontWeight: 600, marginBottom: 2 }}>Post-Meal</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'visible' }}>
                <span style={{ fontSize: 11, color: '#ff6b6b', minWidth: 28, textAlign: 'right' }}>{(() => { 
                  const data = getSparklineData(getRecentWeeks(weeks, 5), 'postMeal'); 
                  const val = data[0];
                  return (typeof val === 'number' || typeof val === 'string') ? val : '';
                })()}</span>
                <canvas ref={postMealSparkRef} width={110} height={44} style={{ maxWidth: 120, maxHeight: 44, overflow: 'visible', position: 'relative', zIndex: 2 }} />
                <span style={{ fontSize: 11, color: '#ff6b6b', minWidth: 28, textAlign: 'left' }}>{(() => { 
                  const data = getSparklineData(getRecentWeeks(weeks, 5), 'postMeal'); 
                  const val = data[data.length-1];
                  return (typeof val === 'number' || typeof val === 'string') ? val : '';
                })()}</span>
              </div>
            </div>
            {/* Weight */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, overflow: 'visible', position: 'relative' }}>
              <span style={{ fontSize: 11, color: '#388e3c', fontWeight: 600, marginBottom: 2 }}>Weight</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'visible' }}>
                <span style={{ fontSize: 11, color: '#388e3c', minWidth: 28, textAlign: 'right' }}>{(() => { 
                  const data = getSparklineData(getRecentWeeks(weeks, 5), 'weight'); 
                  const val = data[0];
                  return (typeof val === 'number' || typeof val === 'string') ? val : '';
                })()}</span>
                <canvas ref={weightSparkRef} width={110} height={44} style={{ maxWidth: 120, maxHeight: 44, overflow: 'visible', position: 'relative', zIndex: 2 }} />
                <span style={{ fontSize: 11, color: '#388e3c', minWidth: 28, textAlign: 'left' }}>{(() => { 
                  const data = getSparklineData(getRecentWeeks(weeks, 5), 'weight'); 
                  const val = data[data.length-1];
                  return (typeof val === 'number' || typeof val === 'string') ? val : '';
                })()}</span>
              </div>
            </div>
            {/* Waist */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, overflow: 'visible', position: 'relative' }}>
              <span style={{ fontSize: 11, color: '#fbc02d', fontWeight: 600, marginBottom: 2 }}>Waist</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'visible' }}>
                <span style={{ fontSize: 11, color: '#fbc02d', minWidth: 28, textAlign: 'right' }}>{(() => { 
                  const data = getSparklineData(getRecentWeeks(weeks, 5), 'waist'); 
                  const val = data[0];
                  return (typeof val === 'number' || typeof val === 'string') ? val : '';
                })()}</span>
                <canvas ref={waistSparkRef} width={110} height={44} style={{ maxWidth: 120, maxHeight: 44, overflow: 'visible', position: 'relative', zIndex: 2 }} />
                <span style={{ fontSize: 11, color: '#fbc02d', minWidth: 28, textAlign: 'left' }}>{(() => { 
                  const data = getSparklineData(getRecentWeeks(weeks, 5), 'waist'); 
                  const val = data[data.length-1];
                  return (typeof val === 'number' || typeof val === 'string') ? val : '';
                })()}</span>
              </div>
            </div>
            {/* BP */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, overflow: 'visible', position: 'relative' }}>
              <span style={{ fontSize: 11, color: '#d32f2f', fontWeight: 600, marginBottom: 2 }}>BP</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'visible' }}>
                <span style={{ fontSize: 11, color: '#d32f2f', minWidth: 28, textAlign: 'right' }}>{(() => { 
                  const data = getSparklineData(getRecentWeeks(weeks, 5), 'bp'); 
                  const val = data[0];
                  return (val && typeof val === 'object' && 'systolic' in val && 'diastolic' in val) ? `${val.systolic}/${val.diastolic}` : '';
                })()}</span>
                <canvas ref={bpSparkRef} width={110} height={44} style={{ maxWidth: 120, maxHeight: 44, overflow: 'visible', position: 'relative', zIndex: 2 }} />
                <span style={{ fontSize: 11, color: '#d32f2f', minWidth: 28, textAlign: 'left' }}>{(() => { 
                  const data = getSparklineData(getRecentWeeks(weeks, 5), 'bp'); 
                  const last = data[data.length-1];
                  return (last && typeof last === 'object' && 'systolic' in last && 'diastolic' in last) ? `${last.systolic}/${last.diastolic}` : '';
                })()}</span>
              </div>
            </div>
            {/* Exercise */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, overflow: 'visible', position: 'relative' }}>
              <span style={{ fontSize: 11, color: '#8e24aa', fontWeight: 600, marginBottom: 2 }}>Exercise (min)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'visible' }}>
                <span style={{ fontSize: 11, color: '#8e24aa', minWidth: 28, textAlign: 'right' }}>{(() => { 
                  const data = getExerciseMinutesPerWeek(getRecentWeeks(weeks, 5)); 
                  const val = data[0];
                  return (typeof val === 'number' && !isNaN(val)) ? val : '';
                })()}</span>
                <canvas ref={exerciseSparkRef} width={110} height={44} style={{ maxWidth: 120, maxHeight: 44, overflow: 'visible', position: 'relative', zIndex: 2 }} />
                <span style={{ fontSize: 11, color: '#8e24aa', minWidth: 28, textAlign: 'left' }}>{(() => { 
                  const data = getExerciseMinutesPerWeek(getRecentWeeks(weeks, 5)); 
                  const val = data[data.length-1];
                  return (typeof val === 'number' && !isNaN(val)) ? val : '';
                })()}</span>
              </div>
            </div>
          </div>
          {/* End Recent Trends Sparklines */}
          {weeks.map((week, i) => buildWeeklyGridWithAnalysis(week, i))}
          {/* Charts */}
          <div id="chartsSection" style={{ marginTop: 50 }}>
            <h2 style={{ color: '#1e3c72', marginBottom: 30 }}>📊 GLUCOSE TREND CHARTS</h2>
            <div className="chart-container">
              <div className="chart-title">📈 Glucose Trends Over Time</div>
              <div className="chart-wrapper"><canvas ref={glucoseChartRef} /></div>
            </div>
            <div className="chart-container">
              <div className="chart-title">📊 Weekly Averages</div>
              <div className="chart-wrapper"><canvas ref={weeklyChartRef} /></div>
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div className="chart-container" style={{ flex: 1, minWidth: 320 }}>
                <div className="chart-title">📏 Weight & Waist Trend</div>
                <div className="chart-wrapper"><canvas ref={weightWaistChartRef} /></div>
              </div>
              <div className="chart-container" style={{ flex: 1, minWidth: 320 }}>
                <div className="chart-title">🩺 Blood Pressure Trend</div>
                <div className="chart-wrapper"><canvas ref={bpChartRef} /></div>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right', marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                         <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
               <label htmlFor="exportFilename2" style={{ fontWeight: 'bold', color: '#1e3c72', fontSize: 14 }}>Filename:</label>
               <input
                 id="exportFilename2"
                 type="text"
                 value={exportFilename}
                 onChange={(e) => setExportFilename(e.target.value)}
                 style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', minWidth: 200 }}
                 placeholder="Enter filename"
               />
             </div>
            <button className="export-btn" onClick={handleExportPDF}>Download PDF</button>
            <button className="export-btn" onClick={handleExportHTML}>Download HTML</button>
          </div>
        </>
      )}
    </div>
  );
};

export default HealthReportGenerator; 